import { QUESTS, type QuestDef } from "@/content/quests";
import { lt, t } from "@/i18n";
import type { LocalizedText } from "@/math";
import { addItems, applyXp, hero, showToast, type HeroState } from "@/state/store";

export interface QuestDialog {
  lines: LocalizedText[];
  onClose?: () => void;
}

function fill(lines: LocalizedText[], params: Record<string, string | number>): LocalizedText[] {
  const sub = (s: string) =>
    Object.entries(params).reduce((acc, [k, v]) => acc.replaceAll(`{${k}}`, String(v)), s);
  return lines.map((l) => ({ pl: sub(l.pl), en: sub(l.en) }));
}

export function questsOf(giver: string): QuestDef[] {
  return Object.values(QUESTS).filter((q) => q.giver === giver);
}

export function isAvailable(h: HeroState, q: QuestDef): boolean {
  if (h.quests[q.id]) return false;
  if (q.requires && h.quests[q.requires]?.status !== "done") return false;
  return true;
}

export function isComplete(h: HeroState, q: QuestDef): boolean {
  const p = h.quests[q.id];
  return !!p && p.status === "active" && p.progress >= q.objective.count;
}

export function activeQuests(h: HeroState): Array<{ def: QuestDef; progress: number }> {
  return Object.values(QUESTS)
    .filter((q) => h.quests[q.id]?.status === "active")
    .map((q) => ({ def: q, progress: h.quests[q.id]?.progress ?? 0 }));
}

export function acceptQuest(questId: string): void {
  const h = hero.value;
  if (h.quests[questId]) return;
  hero.value = { ...h, quests: { ...h.quests, [questId]: { status: "active", progress: 0 } } };
  showToast(t("quest.accepted", { title: lt(QUESTS[questId]!.title) }));
}

export function completeQuest(questId: string): void {
  const q = QUESTS[questId];
  const h = hero.value;
  if (!q || !isComplete(h, q)) return;
  let next: HeroState = {
    ...h,
    gold: h.gold + q.reward.gold,
    quests: { ...h.quests, [questId]: { status: "done", progress: q.objective.count } },
  };
  if (q.reward.items) next = addItems(next, q.reward.items);
  if (q.reward.flag) next = { ...next, flags: { ...next.flags, [q.reward.flag]: true } };
  const { hero: leveled, leveledUp } = applyXp(next, q.reward.xp);
  hero.value = leveled;
  showToast(t("quest.completed", { title: lt(q.title), xp: q.reward.xp, gold: q.reward.gold }), 4000);
  if (leveledUp) setTimeout(() => showToast(t("battle.levelUp", { lvl: leveled.lvl })), 4100);
}

/**
 * Advance every active quest whose objective matches. `next` computes the new progress from the
 * old one; a toast announces progress (`announce` decides whether an intermediate step is shown).
 */
function advance(
  match: (q: QuestDef) => boolean,
  next: (progress: number, q: QuestDef) => number,
  announce: (progress: number, q: QuestDef) => boolean = () => true,
): void {
  const h = hero.value;
  const quests = { ...h.quests };
  let changed = false;
  let delay = 600;
  for (const q of Object.values(QUESTS)) {
    const p = quests[q.id];
    if (!p || p.status !== "active" || p.progress >= q.objective.count || !match(q)) continue;
    const progress = Math.min(q.objective.count, next(p.progress, q));
    if (progress === p.progress) continue;
    quests[q.id] = { status: "active", progress };
    changed = true;
    const done = progress >= q.objective.count;
    if (done || announce(progress, q)) {
      const text = done
        ? t("quest.readyToTurnIn", { title: lt(q.title) })
        : t("quest.progress", { title: lt(q.title), progress, count: q.objective.count });
      setTimeout(() => showToast(text, 3000), delay);
      delay += 1200;
    }
  }
  if (changed) hero.value = { ...hero.value, quests };
}

/** Called by the battle controller after a victory. */
export function onMonsterDefeated(monsterId: string): void {
  advance(
    (q) => q.objective.kind === "defeat" && q.objective.monsterId === monsterId,
    (p) => p + 1,
  );
}

/** Called by the world when the hero talks to an NPC (delivery quests). */
export function onNpcTalked(npcId: string): void {
  advance(
    (q) => q.objective.kind === "talk" && q.objective.npcId === npcId,
    () => 1,
  );
}

/** Called by the world when a guarded chest is opened. */
export function onChestOpened(chestId: string): void {
  advance(
    (q) => q.objective.kind === "chest" && q.objective.chestId === chestId,
    () => 1,
  );
}

/** Called by the battle controller after every answered question in a battle. */
export function onBattleAnswer(correct: boolean, streak: number): void {
  if (!correct) return;
  advance(
    (q) => q.objective.kind === "correct",
    (p) => p + 1,
    (p) => p % 5 === 0,
  );
  advance(
    (q) => q.objective.kind === "streak",
    (p) => Math.max(p, streak),
    () => false,
  );
}

/**
 * What an NPC should say right now regarding quests: turn-in first, then progress, then a new offer.
 * Returns null when the NPC has nothing quest-related to say.
 */
export function questDialogFor(giver: string): QuestDialog | null {
  const h = hero.value;
  const mine = questsOf(giver);
  const turnIn = mine.find((q) => isComplete(h, q));
  if (turnIn) return { lines: turnIn.complete, onClose: () => completeQuest(turnIn.id) };
  const running = mine.find((q) => h.quests[q.id]?.status === "active");
  if (running) {
    const progress = h.quests[running.id]?.progress ?? 0;
    return { lines: fill(running.inProgress, { progress, count: running.objective.count }) };
  }
  const offer = mine.find((q) => isAvailable(h, q));
  if (offer) return { lines: offer.offer, onClose: () => acceptQuest(offer.id) };
  return null;
}
